import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  Buildings,
  Wrench,
  ChartLineUp,
  Bell,
  DeviceMobile,
  ShieldCheck,
  UsersThree,
  MapTrifold,
  CalendarCheck,
  FileText,
  ArrowRight,
  EnvelopeSimple,
  Globe,
  Lightning,
  Clock,
  TrendUp,
  Warning,
  CheckCircle,
  Gear,
  ChartBar,
  Sparkle,
  Brain,
  ShieldWarning,
  Database,
  Palette,
  ChartPieSlice,
  MagnifyingGlass
} from '@phosphor-icons/react';

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1771922748624-b205cf5d002d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxob3RlbCUyMG1haW50ZW5hbmNlJTIwdGVjaG5vbG9neSUyMGRhc2hib2FyZCUyMGRhcmt8ZW58MHx8fHwxNzc2MTQ3OTE2fDA&ixlib=rb-4.1.0&q=85',
  dashboard: 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBkYXNoYm9hcmQlMjBhbmFseXRpY3MlMjBtb25pdG9yJTIwb2ZmaWNlfGVufDB8fHx8MTc3NjE0NzU1Nnww&ixlib=rb-4.1.0&q=85',
  tablet: 'https://images.unsplash.com/photo-1737442528819-5526652236e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHx0YWJsZXQlMjBhbmFseXRpY3MlMjBkYXRhJTIwZ3JhcGhzJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc2MTQ3NTU2fDA&ixlib=rb-4.1.0&q=85',
  screens: 'https://images.unsplash.com/photo-1767424412548-1a1ac7f4b9bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHx0YWJsZXQlMjBhbmFseXRpY3MlMjBkYXRhJTIwZ3JhcGhzJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc2MTQ3NTU2fDA&ixlib=rb-4.1.0&q=85',
  meeting: 'https://images.unsplash.com/photo-1758691736542-c437fea2c673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMGFuYWx5emluZyUyMGRhdGElMjBzY3JlZW4lMjBvZmZpY2V8ZW58MHx8fHwxNzc2MTQ3OTE2fDA&ixlib=rb-4.1.0&q=85',
  heatmap: 'https://static.prod-images.emergentagent.com/jobs/078e2562-c185-4084-b267-54d71eb0aaa9/images/2998eafc2204022f0cc2130ebe259463a5401fa1f952f6f036fdfde87b54cdcf.png',
};

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const CountUp = ({ target, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = React.useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 2000;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

const InvestorLandingPage = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Buildings, title: 'Multi-Hotel & Cluster', desc: 'Gestione centralizzata di tutti gli hotel con organizzazione per cluster geografici e drill-down su singola struttura.' },
    { icon: Wrench, title: 'Ticket Management', desc: 'Sistema completo di ticket con priorita automatiche basate su occupazione camera e area, assegnazione tecnici e timeline.' },
    { icon: UsersThree, title: '6 Ruoli Operativi', desc: 'Super Admin, Hotel Manager, Capo Manutentore, Tecnico, Housekeeping e Front Office.' },
    { icon: MapTrifold, title: 'Heatmap Problemi', desc: 'Mappa visiva dei problemi ricorrenti per camera e area comune con analisi per piano e pattern temporali.' },
    { icon: CalendarCheck, title: 'Manutenzioni Contrattuali', desc: 'Gestione scadenze ditte esterne, alert automatici, conferme di intervento e tracciamento contratti.' },
    { icon: Bell, title: 'Notifiche Real-Time', desc: 'Push VAPID nativo web + notifiche in-app + email SMTP per assegnazioni, urgenze e aggiornamenti di stato.' },
    { icon: DeviceMobile, title: 'PWA Mobile', desc: 'Progressive Web App installabile su qualsiasi dispositivo. I tecnici lavorano in mobilita senza app store.' },
    { icon: FileText, title: 'Report & Export', desc: 'Analytics avanzate, produttivita tecnici, SLA tracking, confronto periodi e export dati in Excel.' },
    { icon: ShieldCheck, title: 'Sicurezza Enterprise', desc: 'JWT authentication, protezione brute-force, gestione ruoli granulare e audit trail completo.' },
    // ===== NOVITA v2.0 - DATA INTEL SUITE =====
    { icon: Sparkle, title: 'AI Time Estimate', desc: 'Predizione automatica del tempo di risoluzione basata sullo storico ticket simili (categoria + hotel + area).', isNew: true },
    { icon: ChartPieSlice, title: 'Heatmap Temporale', desc: 'Matrice 7 giorni × 24 ore che rivela i picchi di lavoro. Ottimizza turni e staffing con dati reali.', isNew: true },
    { icon: MagnifyingGlass, title: 'Genealogia Asset', desc: 'Storico completo per camera/area con trend, distribuzione categorie e raccomandazioni ripara-vs-sostituisci.', isNew: true },
    { icon: ShieldWarning, title: 'Sistema Disciplinare', desc: 'Cartellini di ammonizione con soglia automatica "sotto osservazione" al 3° del mese e workflow ex Art. 7.', isNew: true },
    { icon: Brain, title: 'Performance Tecnico', desc: 'Dashboard personale per ogni tecnico con confronto vs media squadra, ranking e trend mensile.', isNew: true },
    { icon: Palette, title: 'Tema Data Intel', desc: 'Interfaccia dark tattica in stile Bloomberg terminal, oltre ai temi Light e Dark classici.', isNew: true },
    { icon: Database, title: 'Backup & Restore', desc: 'Esportazione completa del database in ZIP JSON per backup, audit legali e migrazione tra ambienti.', isNew: true },
  ];

  const metrics = [
    { value: 5872, suffix: '+', label: 'Ticket gestiti in produzione', icon: Wrench },
    { value: 40, suffix: '%', label: 'Riduzione tempi di intervento', icon: Clock },
    { value: 60, suffix: '%', label: 'Meno ticket urgenti ricorrenti', icon: Warning },
    { value: 95, suffix: '%', label: 'Tasso di risoluzione SLA', icon: TrendUp },
  ];

  const advantages = [
    'Nessuna installazione — funziona da browser e PWA',
    'Setup rapido — operativo in 24 ore',
    'Scalabile da 1 a 100+ hotel',
    'Personalizzabile per ogni gruppo alberghiero',
    'Aggiornamenti continui inclusi',
    'Supporto tecnico dedicato',
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="investor-landing">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1e3a5f] rounded-lg flex items-center justify-center">
              <Gear className="w-5 h-5 text-white" weight="bold" />
            </div>
            <div>
              <span className="text-lg font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>HostFix</span>
              <span className="text-xs text-slate-400 ml-1.5">by PTK Solutions</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#problema" className="hover:text-[#1e3a5f] transition-colors">Il Problema</a>
            <a href="#soluzione" className="hover:text-[#1e3a5f] transition-colors">La Soluzione</a>
            <a href="#funzionalita" className="hover:text-[#1e3a5f] transition-colors">Funzionalita</a>
            <a href="#impatto" className="hover:text-[#1e3a5f] transition-colors">Risultati</a>
            <a href="#chi-siamo" className="hover:text-[#1e3a5f] transition-colors">PTK Solutions</a>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#1e3a5f] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#152c4a] transition-all"
            data-testid="hero-login-button"
          >
            Accedi alla Demo
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="HostFix Analytics Dashboard" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/95 via-[#0f172a]/80 to-[#0f172a]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
          <FadeUp>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 mb-6">
              <Lightning className="w-4 h-4 text-[#d4af37]" weight="fill" />
              <span className="text-sm font-semibold text-[#d4af37] uppercase tracking-wider">v2.0 · Data Intel Suite live</span>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl tracking-tighter font-semibold text-white max-w-4xl leading-[1.05]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Manutenzione alberghiera intelligente
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="text-lg md:text-xl text-slate-300 mt-8 max-w-2xl leading-relaxed">
              HostFix e il sistema CMMS progettato da <strong className="text-white">PTK Solutions</strong> per digitalizzare e ottimizzare la gestione manutentiva di gruppi alberghieri. Una piattaforma, tutti i tuoi hotel sotto controllo.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="flex flex-wrap gap-4 mt-10">
              <button
                onClick={() => navigate('/login')}
                className="bg-[#d4af37] text-[#0f172a] px-8 py-4 text-base font-bold hover:bg-[#c4a030] transition-all flex items-center gap-2"
                data-testid="hero-cta-button"
              >
                Richiedi una Demo
                <ArrowRight className="w-5 h-5" weight="bold" />
              </button>
              <a
                href="#soluzione"
                className="border border-white/30 text-white px-8 py-4 text-base font-semibold hover:bg-white/10 transition-all"
              >
                Scopri la piattaforma
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.4}>
            <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/10">
              <div>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>5.872+</p>
                <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Ticket gestiti</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>4</p>
                <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Hotel live</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>6</p>
                <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Ruoli operativi</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>24/7</p>
                <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Real-time monitoring</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>PWA</p>
                <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Mobile ready</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* The Problem */}
      <section id="problema" className="py-24 md:py-32 bg-[#f8fafc]" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1e3a5f] mb-4">Il Problema</p>
              <h2 className="text-4xl md:text-5xl tracking-tight font-medium text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                La manutenzione tradizionale e un costo nascosto
              </h2>
              <div className="space-y-5 text-base md:text-lg leading-relaxed text-slate-600">
                <p>
                  Nella gestione alberghiera tradizionale, le segnalazioni si perdono tra telefonate, messaggi WhatsApp e fogli di carta. Nessun tracciamento, nessuna priorita, nessuna analisi.
                </p>
                <p>
                  Il risultato? Tempi di intervento lunghi, ospiti insoddisfatti, costi che lievitano e nessuna visibilita sulle performance del team manutentivo.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-4">
                <div className="p-5 bg-white border border-slate-200 shadow-sm text-center">
                  <p className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Outfit, sans-serif' }}>72h</p>
                  <p className="text-xs text-slate-500 mt-1">Tempo medio risoluzione</p>
                </div>
                <div className="p-5 bg-white border border-slate-200 shadow-sm text-center">
                  <p className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Outfit, sans-serif' }}>35%</p>
                  <p className="text-xs text-slate-500 mt-1">Ticket persi o duplicati</p>
                </div>
                <div className="p-5 bg-white border border-slate-200 shadow-sm text-center">
                  <p className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Outfit, sans-serif' }}>0%</p>
                  <p className="text-xs text-slate-500 mt-1">Visibilita sui dati</p>
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="relative">
                <img src={IMAGES.meeting} alt="Team analyzing data" className="w-full h-[480px] object-cover shadow-2xl" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section id="soluzione" className="py-24 md:py-32" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <div className="relative">
                <img src={IMAGES.dashboard} alt="HostFix Dashboard" className="w-full h-[480px] object-cover shadow-2xl" />
                <div className="absolute -bottom-6 -right-6 bg-[#1e3a5f] text-white p-6 shadow-xl max-w-xs">
                  <p className="text-sm font-bold uppercase tracking-wider text-[#d4af37] mb-1">Risultato</p>
                  <p className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Tempi di intervento ridotti del 40%</p>
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1e3a5f] mb-4">La Soluzione</p>
              <h2 className="text-4xl md:text-5xl tracking-tight font-medium text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                HostFix: tutto sotto controllo
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8">
                HostFix digitalizza l'intero ciclo di vita della manutenzione alberghiera: dalla segnalazione alla risoluzione, dalle analytics ai report per il management.
              </p>
              <div className="space-y-4">
                {advantages.map((adv, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10b981] flex-shrink-0" weight="fill" />
                    <span className="text-slate-700 font-medium">{adv}</span>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section id="funzionalita" className="py-24 md:py-32 bg-[#f8fafc]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1e3a5f] mb-4">Funzionalita</p>
              <h2 className="text-4xl md:text-5xl tracking-tight font-medium text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                10 moduli integrati
              </h2>
              <p className="text-lg text-slate-500 mt-4 max-w-2xl mx-auto">
                Ogni modulo e progettato per risolvere un problema specifico della gestione manutentiva alberghiera.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-12 gap-6 md:gap-8">
            {/* Large feature - Multi-Hotel */}
            <FadeUp className="col-span-12 md:col-span-8">
              <div className="relative h-full min-h-[320px] bg-[#1e3a5f] rounded-2xl p-8 md:p-12 overflow-hidden group">
                <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
                  <img src={IMAGES.screens} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10">
                  <Buildings className="w-10 h-10 text-[#d4af37] mb-6" weight="duotone" />
                  <h3 className="text-2xl md:text-3xl font-medium text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Gestione Multi-Hotel e Cluster
                  </h3>
                  <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-lg">
                    Dashboard centralizzata per monitorare 20+ hotel simultaneamente. Organizzazione per cluster geografici con drill-down su singola struttura, piano e camera.
                  </p>
                </div>
              </div>
            </FadeUp>

            {/* Heatmap */}
            <FadeUp delay={0.1} className="col-span-12 md:col-span-4">
              <div className="relative h-full min-h-[320px] rounded-2xl overflow-hidden group">
                <img src={IMAGES.heatmap} alt="Heatmap" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/90 to-[#0f172a]/20" />
                <div className="relative z-10 p-8 flex flex-col justify-end h-full">
                  <MapTrifold className="w-8 h-8 text-[#d4af37] mb-4" weight="duotone" />
                  <h3 className="text-xl font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Heatmap Problemi</h3>
                  <p className="text-slate-300 text-sm mt-2">Identifica le aree critiche prima che diventino emergenze.</p>
                </div>
              </div>
            </FadeUp>

            {/* Remaining features in 3-col grid */}
            {features.slice(2).map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.05} className="col-span-12 sm:col-span-6 md:col-span-4">
                <div className={`relative h-full bg-white border rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group ${f.isNew ? 'border-[#d4af37]/60 ring-1 ring-[#d4af37]/20' : 'border-slate-200'}`}>
                  {f.isNew && (
                    <span className="absolute top-3 right-3 bg-gradient-to-r from-[#d4af37] to-[#f4c430] text-[#0f172a] text-[10px] font-extrabold px-2 py-0.5 rounded-full tracking-wider">
                      NUOVO
                    </span>
                  )}
                  <f.icon className="w-8 h-8 text-[#1e3a5f] mb-5 group-hover:text-[#d4af37] transition-colors" weight="duotone" />
                  <h3 className="text-lg font-semibold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Data Intel Suite v2.0 Spotlight */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-[#0a1220] via-[#0f172a] to-[#1e293b] overflow-hidden" data-testid="data-intel-section">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, #d4af37 0%, transparent 50%), radial-gradient(circle at 75% 75%, #3b82f6 0%, transparent 50%)',
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeUp>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-[#d4af37]/10 border border-[#d4af37]/30 px-4 py-1.5 mb-6">
                <Sparkle className="w-4 h-4 text-[#d4af37]" weight="fill" />
                <span className="text-xs font-bold text-[#d4af37] uppercase tracking-widest">Release 2026 · v2.0</span>
              </div>
              <h2 className="text-4xl md:text-6xl tracking-tight font-medium text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Data Intel Suite
              </h2>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
                Con oltre <strong className="text-white">5.800 ticket reali</strong> processati in produzione, HostFix trasforma lo storico in <strong className="text-[#d4af37]">decisioni concrete</strong>. Cinque nuove funzioni AI e analytics disponibili da oggi.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {/* Large: AI Time Estimate */}
            <FadeUp className="col-span-12 md:col-span-7">
              <div className="h-full bg-gradient-to-br from-[#1e3a5f] to-[#152c4a] rounded-2xl p-8 md:p-10 border border-white/10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#d4af37]/20 flex items-center justify-center shrink-0">
                    <Sparkle className="w-7 h-7 text-[#d4af37]" weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-[#d4af37] uppercase tracking-wider">AI Prediction</p>
                    <h3 className="text-2xl md:text-3xl font-semibold text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Tempo di risoluzione stimato in tempo reale
                    </h3>
                  </div>
                </div>
                <p className="text-slate-300 text-base leading-relaxed mb-6">
                  Quando la reception apre un nuovo ticket, il sistema calcola automaticamente la <strong className="text-white">stima del tempo</strong> basandosi sui ticket storici simili. L&apos;ospite sa subito cosa aspettarsi.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <p className="text-[10px] uppercase text-slate-400 font-bold">Media</p>
                    <p className="text-xl font-extrabold text-white mt-1">2h 15m</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <p className="text-[10px] uppercase text-slate-400 font-bold">Range</p>
                    <p className="text-xl font-extrabold text-white mt-1">1h - 4h</p>
                  </div>
                  <div className="bg-[#d4af37]/10 rounded-lg p-3 border border-[#d4af37]/30">
                    <p className="text-[10px] uppercase text-[#d4af37] font-bold">Confidence</p>
                    <p className="text-xl font-extrabold text-[#d4af37] mt-1">Alta</p>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Heatmap */}
            <FadeUp delay={0.1} className="col-span-12 md:col-span-5">
              <div className="h-full bg-gradient-to-br from-[#7c2d12] to-[#450a0a] rounded-2xl p-8 border border-orange-500/20 relative overflow-hidden">
                <ChartPieSlice className="w-10 h-10 text-orange-400 mb-4" weight="duotone" />
                <p className="text-xs font-bold text-orange-400 uppercase tracking-wider">Analytics</p>
                <h3 className="text-xl md:text-2xl font-semibold text-white mt-1 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Heatmap Temporale
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  Matrice 7 × 24 che rivela i picchi settimanali. Ottimizza turni e staffing con dati reali.
                </p>
                <div className="mt-4 flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded font-bold border border-red-500/30">Picco: Sab 11:00</span>
                </div>
              </div>
            </FadeUp>

            {/* Genealogia Asset */}
            <FadeUp delay={0.15} className="col-span-12 md:col-span-4">
              <div className="h-full bg-gradient-to-br from-[#312e81] to-[#1e1b4b] rounded-2xl p-8 border border-indigo-500/20">
                <MagnifyingGlass className="w-10 h-10 text-indigo-300 mb-4" weight="duotone" />
                <p className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Asset Intelligence</p>
                <h3 className="text-xl font-semibold text-white mt-1 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Genealogia Camera
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Storico completo di ogni asset con raccomandazioni <strong className="text-white">ripara-vs-sostituisci</strong> basate su MTBF reale.
                </p>
              </div>
            </FadeUp>

            {/* Disciplina */}
            <FadeUp delay={0.2} className="col-span-12 md:col-span-4">
              <div className="h-full bg-gradient-to-br from-[#7c2d12] to-[#78350f] rounded-2xl p-8 border border-amber-500/20">
                <ShieldWarning className="w-10 h-10 text-amber-300 mb-4" weight="duotone" />
                <p className="text-xs font-bold text-amber-300 uppercase tracking-wider">HR & Compliance</p>
                <h3 className="text-xl font-semibold text-white mt-1 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sistema Disciplinare
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Cartellini di ammonizione con workflow ex Art. 7. Soglia automatica <strong className="text-white">&quot;sotto osservazione&quot;</strong> al 3° del mese.
                </p>
              </div>
            </FadeUp>

            {/* Performance Tecnico */}
            <FadeUp delay={0.25} className="col-span-12 md:col-span-4">
              <div className="h-full bg-gradient-to-br from-[#064e3b] to-[#022c22] rounded-2xl p-8 border border-emerald-500/20">
                <Brain className="w-10 h-10 text-emerald-300 mb-4" weight="duotone" />
                <p className="text-xs font-bold text-emerald-300 uppercase tracking-wider">Performance</p>
                <h3 className="text-xl font-semibold text-white mt-1 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Dashboard Tecnici
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Ogni tecnico ha una dashboard personale con confronto <strong className="text-white">tempi vs media squadra</strong>, ranking e trend mensile.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section id="impatto" className="relative py-24 md:py-32 overflow-hidden" data-testid="metrics-section">
        <div className="absolute inset-0">
          <img src={IMAGES.tablet} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0f172a]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-sm uppercase tracking-[0.25em] font-bold text-[#d4af37] mb-4">Risultati Misurabili</p>
              <h2 className="text-4xl md:text-5xl tracking-tight font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Impatto concreto sulle operations
              </h2>
              <p className="text-slate-400 mt-4 max-w-xl mx-auto">
                Dati basati sull'implementazione reale in strutture alberghiere italiane.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((m, i) => (
              <FadeUp key={m.label} delay={i * 0.1}>
                <div className="text-center p-8 border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all">
                  <m.icon className="w-8 h-8 text-[#d4af37] mx-auto mb-4" weight="duotone" />
                  <p className="text-5xl md:text-6xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <CountUp target={m.value} suffix={m.suffix} />
                  </p>
                  <p className="text-sm text-slate-400 leading-snug">{m.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32" data-testid="workflow-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeUp>
            <div className="text-center mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1e3a5f] mb-4">Workflow</p>
              <h2 className="text-4xl md:text-5xl tracking-tight font-medium text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Dal problema alla soluzione in 4 step
              </h2>
            </div>
          </FadeUp>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Segnalazione', desc: 'Housekeeping o Front Office segnalano il problema dalla PWA mobile con pochi tocchi. Priorita calcolata automaticamente.' },
              { step: '02', title: 'Assegnazione', desc: 'Il Capo Manutentore o il sistema assegna il tecnico piu adatto. Notifica istantanea via app e email.' },
              { step: '03', title: 'Intervento', desc: 'Il tecnico prende in carico, aggiorna lo stato in tempo reale e documenta l\'intervento con note e foto.' },
              { step: '04', title: 'Analisi', desc: 'Dashboard analytics tracciano KPI, SLA, produttivita e pattern per decisioni data-driven.' },
            ].map((s, i) => (
              <FadeUp key={s.step} delay={i * 0.1}>
                <div className="relative">
                  <span className="text-8xl font-bold text-slate-100 absolute -top-6 -left-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.step}</span>
                  <div className="relative z-10 pt-12">
                    <div className="w-12 h-0.5 bg-[#d4af37] mb-4" />
                    <h3 className="text-xl font-semibold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* About PTK Solutions */}
      <section id="chi-siamo" className="py-24 md:py-32 bg-[#f8fafc]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <img src={IMAGES.screens} alt="PTK Solutions Technology" className="w-full h-[400px] object-cover shadow-xl" />
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1e3a5f] mb-4">Chi Siamo</p>
              <h2 className="text-4xl md:text-5xl tracking-tight font-medium text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                PTK Solutions
              </h2>
              <div className="space-y-4 text-base md:text-lg leading-relaxed text-slate-600">
                <p>
                  <strong className="text-[#0f172a]">PTK Solutions</strong> sviluppa soluzioni software enterprise per il settore hospitality. HostFix nasce dall'esperienza diretta nel campo della gestione alberghiera e dalla necessita di strumenti digitali all'altezza delle sfide operative quotidiane.
                </p>
                <p>
                  La piattaforma e gia operativa presso il gruppo <strong className="text-[#0f172a]">Invest Hospitality</strong> e disponibile per qualsiasi gruppo alberghiero che voglia trasformare la propria gestione manutentiva.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/login')}
                  className="bg-[#1e3a5f] text-white px-8 py-4 text-base font-bold hover:bg-[#152c4a] transition-all flex items-center gap-2"
                  data-testid="about-cta-button"
                >
                  Accedi alla Piattaforma
                  <ArrowRight className="w-5 h-5" weight="bold" />
                </button>
                <a
                  href="mailto:info@ptkserv.it"
                  className="border border-[#1e3a5f] text-[#1e3a5f] px-8 py-4 text-base font-semibold hover:bg-[#1e3a5f]/5 transition-all flex items-center gap-2"
                >
                  <EnvelopeSimple className="w-5 h-5" />
                  Contattaci
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1e3a5f]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <FadeUp>
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pronto a digitalizzare la manutenzione del tuo gruppo?
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Contattaci per una demo personalizzata della piattaforma HostFix.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:info@ptkserv.it"
                className="bg-[#d4af37] text-[#0f172a] px-8 py-4 text-base font-bold hover:bg-[#c4a030] transition-all flex items-center gap-2"
              >
                <EnvelopeSimple className="w-5 h-5" weight="bold" />
                info@ptkserv.it
              </a>
              <button
                onClick={() => navigate('/login')}
                className="border border-white/30 text-white px-8 py-4 text-base font-semibold hover:bg-white/10 transition-all"
              >
                Accedi alla Demo
              </button>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f172a] py-16" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                  <Gear className="w-5 h-5 text-[#d4af37]" weight="bold" />
                </div>
                <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>HostFix</span>
                <span className="text-xs text-slate-500">by PTK Solutions</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Il CMMS creato per il settore hospitality.<br />
                Manutenzione alberghiera intelligente.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Contatti</h4>
              <div className="space-y-3 text-sm text-slate-400">
                <a href="mailto:info@ptkserv.it" className="flex items-center gap-2 hover:text-white transition-colors">
                  <EnvelopeSimple className="w-4 h-4 text-[#d4af37]" /> info@ptkserv.it
                </a>
                <p className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#d4af37]" /> PTK Solutions</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Piattaforma</h4>
              <div className="space-y-3 text-sm text-slate-400">
                <button onClick={() => navigate('/login')} className="hover:text-white transition-colors flex items-center gap-1">
                  Accedi alla Demo <ArrowRight className="w-3 h-3" />
                </button>
                <a href="#funzionalita" className="block hover:text-white transition-colors">Funzionalita</a>
                <a href="#impatto" className="block hover:text-white transition-colors">Risultati</a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 text-center">
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} PTK Solutions &middot; HostFix &middot; Tutti i diritti riservati</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InvestorLandingPage;
